import axios, { AxiosInstance } from 'axios'

// Get API URL from environment - should point to /api/v3 for admin endpoints
const API_BASE_URL = import.meta.env.VITE_API_URL

if (!API_BASE_URL) {
  const isDev = import.meta.env.DEV
  if (isDev) {
    console.error('❌ VITE_API_URL environment variable is not set!')
    console.error('📝 Please create a .env file in the admin directory with:')
    console.error('   VITE_API_URL=http://localhost:5001/api/v3')
    console.error('   VITE_PORT=3001')
  }
  throw new Error(
    'VITE_API_URL environment variable is not set. ' +
    'Please create a .env file in the admin directory with VITE_API_URL=http://localhost:5001/api/v3'
  )
}

// For non-v3 endpoints (auth, etc.)
const API_BASE_URL_V1 = API_BASE_URL.replace('/v3', '')

// V3 API instance (main - for admin endpoints)
export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// V1 API instance (for auth endpoints)
export const apiV1: AxiosInstance = axios.create({
  baseURL: API_BASE_URL_V1,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor for V3 API
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Request interceptor for V1 API (auth endpoints)
apiV1.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

/**
 * Pulls the user-facing error message out of an axios error.
 *
 * Our backend returns errors in two shapes depending on the endpoint:
 *   - { success: false, error: { message, details? } }   (most v3 routes)
 *   - { success: false, message }                        (legacy)
 *
 * This helper checks every reasonable location and falls back to axios's own
 * message. It also flattens Joi validation errors into a readable string so
 * toasts always have something useful to show.
 */
export const extractApiErrorMessage = (error: any, fallback = 'Something went wrong'): string => {
  const data = error?.response?.data
  if (data) {
    if (typeof data.message === 'string' && data.message.trim()) return data.message
    if (data.error) {
      if (typeof data.error === 'string' && data.error.trim()) return data.error
      if (typeof data.error.message === 'string' && data.error.message.trim()) return data.error.message
      if (typeof data.error.details === 'string' && data.error.details.trim()) return data.error.details
    }
    if (Array.isArray(data.errors) && data.errors.length > 0) {
      const first = data.errors[0]
      if (typeof first === 'string') return first
      if (first?.message) return first.message
    }
  }
  if (typeof error?.message === 'string' && error.message.trim() && error.message !== 'Network Error') {
    return error.message
  }
  return fallback
}

const buildResponseInterceptor =
  () =>
  (error: any) => {
    if (error?.response?.status === 401) {
      // Only redirect if not already on login page
      if (!window.location.pathname.includes('/login')) {
        localStorage.removeItem('adminToken')
        localStorage.removeItem('refreshToken')
        window.location.href = '/login'
      }
    }
    // Attach a normalized message so callers can do `error.message` reliably
    // without having to dig through the response shape themselves.
    try {
      const normalized = extractApiErrorMessage(error, error?.message || 'Request failed')
      if (normalized) {
        error.message = normalized
      }
    } catch {
      // best-effort, never block the rejection
    }
    return Promise.reject(error)
  }

api.interceptors.response.use((response) => response, buildResponseInterceptor())
apiV1.interceptors.response.use((response) => response, buildResponseInterceptor())